import { useState } from 'react';
import { X, PenLine, AlertCircle, ClipboardList } from 'lucide-react';
import type { Asset, KitItem, Station } from '@hcc/types';
import { usePerson, type PersonApi } from '../../hooks/usePeople';
import { useAssetsList } from '../../hooks/useAssets';
import { useHandoverForm } from '../../hooks/useKitHandover';
import { SignaturePadModal } from './SignaturePadModal';
import { AssetPickerModal } from './AssetPickerModal';
import { EquipmentPanel } from './EquipmentPanel';
import { PersonContextPanel } from './PersonContextPanel';
import { HandoverFormModal } from './handover/HandoverFormModal';
import { KitItemsSection } from './KitItemsSection';
import { ConfirmPanel } from './ConfirmPanel';
import { useKitIssueItems } from './useKitIssueItems';
import { useKitConfirmItems, confirmRowsToItems } from './useKitConfirmItems';
import { useKitIssueSubmit } from './useKitIssueSubmit';
import { useKitDraftLoad } from './useKitDraftSync';
import { useKitDrawerDraft } from './useKitDrawerDraft';
import { KitDraftBanner } from './KitDraftBanner';
import { Button } from '../ui/Button';

interface KitIssueDrawerProps {
  station: Station;
  person: PersonApi;
  /** The person's effective kit items, or null when no template matches. */
  templateItems: KitItem[] | null;
  onClose: () => void;
}

/**
 * Right-side drawer for issuing kit to one person at one station: item rows
 * seeded from their resolved kit, pre-assigned tracked assets, an ad-hoc add
 * path, and a signature capture that writes one KitIssued event.
 */
export function KitIssueDrawer({ station, person, templateItems, onClose }: KitIssueDrawerProps) {
  const [signing, setSigning] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Most kit is pre-allocated, so the at-desk "add more" affordances
  // stay tucked away by default. Open them up when there's no template
  // for this position (manual entry is then the only path).
  const [showAddKit, setShowAddKit] = useState(!templateItems);

  // Any half-finished issue left by another desk (or by this one earlier).
  // Seeding waits on it, so nothing below renders template state first.
  const draftLoad = useKitDraftLoad(person.id, station);
  const kit = useKitIssueItems(person.id, station, draftLoad.seed);
  const confirm = useKitConfirmItems(person.id, station, draftLoad.seed);
  const draft = useKitDrawerDraft({
    personId: person.id,
    station,
    load: draftLoad,
    items: kit.items,
    assetCategoryById: kit.assetCategoryById,
    confirmRows: confirm.rows,
    listsSeeded: kit.seeded && confirm.seeded,
  });
  const { notes, setNotes } = draft;

  const submit = useKitIssueSubmit({
    station,
    person,
    clientEventId: draftLoad.clientEventId,
    onDone: () => {
      // The server clears the draft off the back of the KitIssued event;
      // tell the autosave so its unmount flush doesn't recreate it.
      draft.markSubmitted();
      onClose();
    },
  });

  // Pull the full state so the operator sees flag reasons inline
  // before issuing kit. Per CLAUDE.md 6.2 flags must be visible on
  // every station screen for the person until cleared.
  const personFull = usePerson(person.id);
  // Pre-assigned assets for this person at this station. Confirm-on-arrival
  // assets (TUPE) are handled by the confirm panel, so the normal Equipment
  // panel only offers the rest.
  const preAssigned = useAssetsList({ station, assignee: person.id });
  const normalAssets = (preAssigned.data ?? []).filter(
    (a) => !(a.confirm_on_arrival && a.status === 'assigned'),
  );

  // When this station has a published handover checklist, completing goes
  // through the checklist modal (checklist + issuance in one signed step)
  // instead of the plain signature pad.
  const handover = useHandoverForm(station);
  const [handoverOpen, setHandoverOpen] = useState(false);
  // Full asset rows seen in this drawer session (pre-assigned or picked),
  // so the handover form can prefill make / model / registration.
  const [seenAssets, setSeenAssets] = useState<Record<string, Asset>>({});
  function rememberAsset(asset: Asset) {
    setSeenAssets((m) => ({ ...m, [asset.id]: asset }));
  }
  const handoverForm = handover.data?.form && handover.data.version ? handover.data : null;
  // The server rejects a handover with zero NAMED items, so an untouched
  // blank row must not enable the button. Confirm rows (e.g. a TUPE'd van)
  // ride on the handover too, so they count toward "has something to sign".
  const namedItems = kit.items.filter((i) => i.name.trim().length > 0);
  const handoverItems = [...namedItems, ...confirmRowsToItems(confirm.rows)];

  async function onSignatureConfirm(dataUrl: string) {
    await submit.submit(kit.items, confirm.rows, notes, dataUrl);
    setSigning(false);
  }

  // Both signing paths need every confirm row resolved before they can
  // complete: an unmarked existing item would leave the record ambiguous.
  const confirmBlocked = confirm.hasRows && !confirm.allResolved;

  return (
    <>
      {/* Backdrop. Tap-anywhere-to-close. */}
      <div
        className="fixed inset-0 z-40 bg-fg/30 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
        aria-hidden
      />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col border-l border-line/10 bg-surface shadow-token-lg animate-slide-in-right">
        <header className="flex items-start justify-between gap-3 border-b border-divider/10 p-5 sm:p-6">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-accent">
              {station}
            </div>
            <h2 className="mt-0.5 truncate text-xl font-semibold tracking-tight text-fg">
              {person.full_name}
            </h2>
            <p className="text-xs text-fg-muted">{person.position ?? 'Position unknown'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-token-sm p-1.5 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          <PersonContextPanel
            personId={person.id}
            personName={person.full_name}
            station={station}
            flags={personFull.data?.state.flags ?? []}
            notes={personFull.data?.state.notes ?? []}
            tasks={personFull.data?.state.tasks ?? []}
          />

          <KitDraftBanner {...draft.bannerProps} />

          {!templateItems ? (
            <div className="mb-4 flex items-start gap-2 rounded-token-sm bg-warning-soft/15 px-3 py-2 text-sm text-warning-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                No kit template for <strong>{person.position ?? 'this position'}</strong>. Add items
                manually below.
              </span>
            </div>
          ) : null}

          {/* Existing kit the person already holds (TUPE): confirm or mark
              not-present rather than issue. Sits above the issue list. */}
          {confirm.hasRows ? (
            <ConfirmPanel
              rows={confirm.rows}
              station={station}
              onOutcome={confirm.setOutcome}
              onServiceDate={confirm.setServiceDate}
              onPhoto={confirm.setPhoto}
              onSerial={confirm.setSerial}
              onRegistration={confirm.setRegistration}
              onAttributes={confirm.setAttributes}
            />
          ) : null}

          {/* Pre-assigned tracked assets are genuine allocation, so they
              surface by default. The empty state is gone — when nothing is
              pre-assigned the operator just signs for the template items. */}
          {normalAssets.length > 0 ? (
            <EquipmentPanel
              station={station}
              preAssigned={normalAssets}
              selectedAssetIds={kit.selectedAssetIds}
              onToggle={(asset) => {
                if (kit.selectedAssetIds.includes(asset.id)) kit.removeAssetRow(asset.id);
                else {
                  kit.addAssetRow(asset);
                  rememberAsset(asset);
                }
              }}
            />
          ) : null}

          <KitItemsSection
            kit={kit}
            station={station}
            showAddKit={showAddKit}
            onToggleAddKit={() => setShowAddKit((v) => !v)}
            onOpenPicker={() => setPickerOpen(true)}
          />

          <h3 className="mt-6 text-sm font-semibold text-fg">
            Notes <span className="font-normal text-fg-dim">(optional)</span>
          </h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Anything worth noting (size mismatch, swap, late return, etc.)"
            className="mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
          />

          {submit.error ? (
            <div className="mt-4 flex items-start gap-2 rounded-token-sm bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{submit.error}</span>
            </div>
          ) : null}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-divider/10 bg-surface-2 p-4 sm:p-5">
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          {handoverForm ? (
            <Button
              variant="success"
              size="md"
              iconLeft={<ClipboardList className="h-4 w-4" />}
              disabled={handoverItems.length === 0 || confirmBlocked}
              onClick={() => setHandoverOpen(true)}
            >
              Complete handover checklist
            </Button>
          ) : (
            <Button
              variant="success"
              size="md"
              iconLeft={<PenLine className="h-4 w-4" />}
              disabled={(kit.items.length === 0 && !confirm.hasRows) || confirmBlocked}
              onClick={() => setSigning(true)}
            >
              Complete with signature
            </Button>
          )}
        </footer>
      </div>

      <SignaturePadModal
        open={signing}
        title={`${person.full_name} - sign for ${station} kit`}
        onClose={() => setSigning(false)}
        onConfirm={onSignatureConfirm}
      />

      {handoverOpen && handoverForm?.form && handoverForm.version ? (
        <HandoverFormModal
          station={station}
          person={person}
          formId={handoverForm.form.id}
          formTitle={handoverForm.form.title}
          schema={handoverForm.version.schema}
          items={handoverItems}
          kitNotes={notes}
          assetsById={seenAssets}
          clientEventId={draftLoad.clientEventId}
          onClose={() => setHandoverOpen(false)}
          onDone={() => {
            draft.markSubmitted();
            setHandoverOpen(false);
            onClose();
          }}
        />
      ) : null}

      {pickerOpen ? (
        <AssetPickerModal
          station={station}
          excludeIds={kit.selectedAssetIds}
          onClose={() => setPickerOpen(false)}
          onPick={(asset) => {
            kit.addAssetRow(asset);
            rememberAsset(asset);
            setPickerOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
